import * as React from 'react'
import { t } from 'ttag'
import NotificationsWrapper from '../../components/Notification/NotificationsWrapper'
import {
  CustomType,
  Gender,
  GetUserDailyDiaryProgress$Request,
  Lang,
  ProgressType,
  Units,
  UserDailyDiaryProgress,
  UserProfileDto,
} from '../../services/api.types'
import { Card } from '../../components/Card/Card'
import {
  Bar,
  VictoryAxis,
  VictoryBar,
  VictoryChart,
  VictoryStringOrNumberCallback,
  VictoryTooltip,
  VictoryVoronoiContainer,
} from 'victory'
// @ts-ignore
import * as d3Scale from 'd3-scale'

import moment from 'moment'
import './Progress.scss'
import { Grid } from '@material-ui/core'
import VictoryTheme from '../../styles/victory-theme'
import YumProgressPeriod, { ProgressPeriod } from '../../components/YumProgressPeriod/YumProgressPeriod'
import { formatThousands } from '../../common/helpers/translations'
import Hidden from '@material-ui/core/Hidden'
import Loader from '../../components/Loader/Loader'
import YumTabs from '../../components/YumTabs/YumTabs'
import YumTab from '../../components/YumTabs/YumTab/YumTab'
import {
  unitsCountDurationMinuteShort,
  unitsCountEnergyKilocalorieShort,
  unitsCountLengthKilometerShort,
  unitsCountLengthMileShort,
  unitsDisplayDurationCalorieLong,
  unitsDisplayDurationMinuteLong,
} from '../../common/helpers/units'
import { currentLang, currentLocale, isRtl } from '../../i18nInit'
import YumInsufficientPermissionsWrapper from '../../components/YumInsufficientPermissions/YumInsufficientPermissionsWrapper'
import YumLogTable from '../../components/YumCustomTable/YumLogTable'
import { getUnit } from '../../services/CustomTypes'
import { darkGray, darkGreen, primaryBlue } from '../../styles/material-ui-theme-v2'
import { getUserCompanies } from '../RegisterUser/store/RegisterUserSelectors'
import { store } from '../../index'
import { IGlobalState } from '../../Root/store/appReducer'

const CLOSE_TICK_HIDE_PERCENTAGE = 25

export interface IProgressProps {
  dailyDiary: UserDailyDiaryProgress[]
  progressType: ProgressType
  customType: CustomType
  progressPeriod: ProgressPeriod
  userDto: UserProfileDto

  setProgressType(payload: ProgressType): void
  setProgressCustomType(payload: CustomType): void
  setProgressPeriod(payload: ProgressPeriod): void
  setOnboardingStatusToActivityData(): void
  getDailyProgress(payload: GetUserDailyDiaryProgress$Request): void
}

export const getProgressType = (progressType: ProgressType, customType: CustomType) => {
  if (progressType === ProgressType.FORMULA) return CustomType.ACTIVITY_INDEX
  if (progressType === ProgressType.DISTANCE || progressType === ProgressType.INTENSIVE_MINUTES) {
    return customType
  }
  return progressType as unknown as CustomType
}

const debounce = <F extends (...args: any[]) => any>(func: F, waitFor: number) => {
  let timeout: ReturnType<typeof setTimeout> | null = null

  const debounced = (...args: Parameters<F>) => {
    if (timeout !== null) {
      clearTimeout(timeout)
      timeout = null
    }
    timeout = setTimeout(() => func(...args), waitFor)
  }

  return debounced as (...args: Parameters<F>) => ReturnType<F>
}

const convertNumbers2English = function (string: any) {
  return string
    .replace(/[\u0660-\u0669]/g, function (c: any) {
      return c.charCodeAt(0) - 0x0660
    })
    .replace(/[\u06f0-\u06f9]/g, function (c: any) {
      return c.charCodeAt(0) - 0x06f0
    })
}

const convertNumbers2Arabic = function (string: any) {
  return String(string).replace(/[0-9]/g, function (c: any) {
    return '٠١٢٣٤٥٦٧٨٩'.charAt(c)
  })
}

const getTickValues = (goalValue: number, maxDomainValue: number) => {
  const ticks = d3Scale.scaleLinear().domain([0, maxDomainValue]).ticks(4)
  if (!ticks.includes(goalValue)) {
    ticks.push(goalValue)
    // We don't want to sort, so the goal will be rendered last and will be the upmost layer.
    //ticks.sort()
  }
  return ticks
}

const getFormattedDates = (startDate: Date, endDate: Date) => {
  let dates = 'N/A'
  if (startDate && endDate) {
    const dateTimeFormat = new Intl.DateTimeFormat(currentLocale, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      timeZone: 'UTC',
    })
    // @ts-ignore
    dates = dateTimeFormat.formatRange(startDate, endDate)
  }
  return dates
}

const getMaxDataValue = (data: any) =>
  Math.max(
    ...data.map(function (d: any) {
      return d['sum']
    }),
  ) * 1.1

const getAge = (dateOfBirth: string) => {
  const today = new Date()
  const birthDate = new Date(dateOfBirth)
  let age = today.getFullYear() - birthDate.getFullYear()
  const m = today.getMonth() - birthDate.getMonth()
  if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) age--
  return age
}

const PASSIVE_TO_TOTAL_CALORIES_MULTIPLIER = 1.55
const FEMALE_CALORIES_CONSTANT = -161
const MALE_CALORIES_CONSTANT = 5
const getUserCaloriesGoal = (userDto: UserProfileDto) => {
  let goal = 2500
  if (userDto) {
    const { weight, height, dateOfBirth, gender } = userDto
    if (weight && height && dateOfBirth && gender) {
      const age = getAge(dateOfBirth)
      const passiveCalories =
        10 * weight +
        6.25 * (height / 100) * 100 -
        5 * age +
        (gender === Gender.MALE ? MALE_CALORIES_CONSTANT : FEMALE_CALORIES_CONSTANT)
      goal = Math.round(PASSIVE_TO_TOTAL_CALORIES_MULTIPLIER * passiveCalories)
    }
  }
  return goal
}

const getGoalValue = (
  progressType: ProgressType,
  customType: CustomType,
  userDto: UserProfileDto,
  showMiles?: boolean,
  customStepsGoal?: number | null,
) => {
  switch (progressType) {
    case ProgressType.STEPS:
      return customStepsGoal || 12000
    case ProgressType.DISTANCE:
      switch (customType) {
        case CustomType.CYCLING_KM:
          return showMiles ? 10 : 16
        case CustomType.SWIMMING_M:
          return 1000
        default:
          return showMiles ? 6 : 8
      }
    case ProgressType.CALORIES:
      return getUserCaloriesGoal(userDto)
    case ProgressType.INTENSIVE_MINUTES:
      return 20
    case ProgressType.FORMULA:
      return 100
    default:
      return 1
  }
}

const getMaxDomainValue = (progressType: ProgressType, data: any, maxGoalValue: number) => {
  const maxDataValue = getMaxDataValue(data)
  let maxDomainValue = maxGoalValue
    ? maxGoalValue * 2
    : progressType === ProgressType.FORMULA
    ? 200
    : progressType === ProgressType.CALORIES
    ? 3000
    : progressType === ProgressType.STEPS
    ? 20000
    : 40
  if (maxDataValue > maxDomainValue) maxDomainValue = maxDataValue
  return maxDomainValue
}

const processDailyDiary = (
  dailyDiary: UserDailyDiaryProgress[],
  startDate: Date,
  endDate: Date,
): UserDailyDiaryProgress[] => {
  if (!dailyDiary || !dailyDiary.length) return []
  const startDay = moment.utc(startDate).locale('en').format('YYYY-MM-DD')
  const endDay = moment.utc(endDate).locale('en').format('YYYY-MM-DD')
  const firstDate = dailyDiary[0].date
  const lastDate = dailyDiary[dailyDiary.length - 1].date
  if (firstDate === startDay && lastDate === endDay) return dailyDiary
  return []
}

export default function Progress(props: IProgressProps) {
  const { userDto, progressType, customType, progressPeriod, dailyDiary } = props
  const hasActivityIndex = !!userDto.dateOfBirth

  /*const { serviceProvider: serviceProviderRaw } = userDto
  const distanceScopeMissing =
    serviceProviderRaw === ServiceProvider.GOOGLE_FIT &&
    userIntegrationScopes != '' &&
    !userIntegrationScopes.includes('https://www.googleapis.com/auth/fitness.location.read')
  const distanceIntegrationUrl = `${ROUTES.INTEGRATION_PROXY.to}?type=${serviceProviderRaw}&scope=LEAN&d=1`
  const serviceProvider = getServiceProviderLabel(serviceProviderRaw)*/

  /*const handleGoogleFitUnverified = async () => {
    const { value: consent } = await Dialog.confirm({
      title: 'Google Fit',
      message: `Google has not verified our app yet for syncing distance data. You will soon see a screen stating it. There you need to click "Advanced" and "Go to YuMuuv (unsafe)" to continue.`,
      okButtonTitle: t`OK`,
      cancelButtonTitle: t`Cancel`,
    })
    if (!consent) return
    store.dispatch(push(distanceIntegrationUrl))
  }*/

  const showMiles = userDto.units === Units.IMPERIAL
  const now = new Date()
  const [startDate, setStartDate] = React.useState(now)
  const [endDate, setEndDate] = React.useState(now)
  const [dimensions, setDimensions] = React.useState({
    height: window.innerHeight,
    width: window.innerWidth,
  })

  const isMobile = dimensions.width < 576
  const isTablet = dimensions.width < 992
  const chartFontSize = isMobile ? 13 : 5
  const chartRightPadding = isMobile ? 45 : isTablet ? 25 : 20
  const chartLeftPadding = isMobile ? 10 : 5

  const chartStrokeWidth = isMobile ? 2 : 1
  const chartTooltipFontSize = isMobile ? 10 : 5
  const chartTooltipStrokeWidth = isMobile ? 1.5 : 0.75

  const chartTooltipCornerRadius = isMobile ? 4 : 2
  const chartTooltipPointerWidth = isMobile ? 8 : 4
  const chartTooltipPointerLength = isMobile ? 7 : 3.5
  const chartTooltipFlyoutPadding = isMobile
    ? { top: 3.5, bottom: 3.5, left: 6, right: 6 }
    : { top: 1.75, bottom: 1.75, left: 3, right: 3 }
  const chartTooltipDy = isMobile ? -8 : -4

  const syncDailyProgress = (startDate: Date, endDate: Date) => {
    props.getDailyProgress({
      startDate: moment.utc(startDate).locale('en').format('YYYY-MM-DD'),
      endDate: moment.utc(endDate).locale('en').format('YYYY-MM-DD'),
      customType: getProgressType(progressType, customType),
    })
  }

  // Refresh view after front-end sync.
  React.useEffect(() => {
    if (startDate !== endDate) {
      syncDailyProgress(startDate, endDate)
    }
  }, [progressType, customType, userDto.lastSync])

  React.useEffect(() => {
    if (userDto.units === Units.IMPERIAL) {
      if (
        [CustomType.DISTANCE_KM, CustomType.WALKING_KM, CustomType.RUNNING_KM, CustomType.CYCLING_KM].includes(
          customType,
        )
      ) {
        props.setProgressCustomType(CustomType.DISTANCE_MI)
      }
    } else {
      if (
        [CustomType.DISTANCE_MI, CustomType.WALKING_MI, CustomType.RUNNING_MI, CustomType.CYCLING_MI].includes(
          customType,
        )
      ) {
        props.setProgressCustomType(CustomType.DISTANCE_KM)
      }
    }
  }, [userDto.units])

  React.useEffect(() => {
    const debouncedHandleResize = debounce(function handleResize() {
      setDimensions({
        height: window.innerHeight,
        width: window.innerWidth,
      })
    }, 400)

    window.addEventListener('resize', debouncedHandleResize)

    return () => {
      window.removeEventListener('resize', debouncedHandleResize)
    }
  })

  const onProgressTypeChange = (event: React.ChangeEvent<unknown>, progressType: ProgressType) => {
    props.setProgressType(progressType)
    if (progressType === ProgressType.DISTANCE) {
      props.setProgressCustomType(CustomType.DISTANCE_KM)
    } else if (progressType === ProgressType.INTENSIVE_MINUTES) {
      props.setProgressCustomType(CustomType.INTENSIVE_MINUTES)
    }
  }

  const onCustomTypeChange = (event: React.ChangeEvent<unknown>, customType: CustomType) => {
    props.setProgressCustomType(customType)
  }

  const setProgressPeriodDates = (
    progressPeriod: ProgressPeriod,
    progressPeriodDates: { startDate: Date; endDate: Date },
  ) => {
    const { startDate, endDate } = progressPeriodDates
    setStartDate(startDate)
    setEndDate(endDate)

    props.setProgressPeriod(progressPeriod)
    syncDailyProgress(startDate, endDate)
  }

  const periodDays = moment.utc(endDate).diff(moment.utc(startDate), 'days') + 1

  const [data, setData] = React.useState([] as UserDailyDiaryProgress[])

  React.useEffect(() => {
    const diary = processDailyDiary(dailyDiary, startDate, endDate)
    setData(diary ? (isRtl ? diary.slice(0).reverse() : diary) : [])
  }, [dailyDiary])

  /*const progressValueIndicator =
    progressType === ProgressType.STEPS
      ? 'stepsTotal'
      : progressType === ProgressType.DISTANCE
      ? showMiles
        ? 'distanceMi'
        : 'distanceKm'
      : progressType === ProgressType.CALORIES
      ? 'caloriesTotal'
      : progressType === ProgressType.INTENSIVE_MINUTES
      ? 'intensiveMinutesTotal'
      : 'totalPercentage'*/
  const progressValueIndicator = 'sum'

  // Please change Katriito kliinik company progress tab goal from 12k steps a day to 6k steps a day
  const userCompanies = getUserCompanies(store.getState() as any as IGlobalState)
  const isKatriito =
    userCompanies && userCompanies.some(({ companyId }) => companyId === 'b671988d-b6e3-4fac-b737-7b0bdffbaaf8')

  const showActivities = userCompanies && userCompanies.some((uc) => uc.scope?.includes('showActivities'))

  const customStepsGoal = isKatriito ? 6000 : null
  const goalValue = getGoalValue(progressType, customType, userDto, showMiles, customStepsGoal)
  const maxDomainValue = getMaxDomainValue(progressType, data, goalValue)
  const tickValues = getTickValues(goalValue, maxDomainValue)

  // @ts-ignore
  const yAxisTickLabelsFill: VictoryStringOrNumberCallback = ({ tick }) =>
    tick === goalValue ? darkGreen : 'rgba(36, 40, 78, 0.5)'

  // @ts-ignore
  const yAxisGridStroke: VictoryStringOrNumberCallback = ({ tick }) => (tick === goalValue ? darkGreen : '#ECEFF1')

  const responsiveStrokeWidthMultiplier = isMobile ? 1 : 2

  // @ts-ignore
  const yAxisGridStrokeWidth: VictoryStringOrNumberCallback = ({ tick }) =>
    tick === goalValue ? chartStrokeWidth * responsiveStrokeWidthMultiplier : chartStrokeWidth

  // @ts-ignore
  const yAxisGridStrokeDasharray: VictoryStringOrNumberCallback = ({ tick }) => (tick === goalValue ? '5' : null)

  const yAxisTickFormat = (tickValue: any) => {
    // Hide y-axis tick values (gray numbers) that are too close to the goal value (green numbers).
    if (
      tickValue !== goalValue &&
      Math.abs(tickValue - goalValue) < tickValue / ((1 / CLOSE_TICK_HIDE_PERCENTAGE) * 100)
    ) {
      return ''
    }
    if (isRtl) {
      //ticks = ticks.map((t) => convertNumbers2Arabic(String(t)))
      /*return progressType === ProgressType.FORMULA
        ? convertNumbers2Arabic(tickValue + '%')
        : progressType === ProgressType.CALORIES
        ? convertNumbers2Arabic(Math.round(tickValue))
        : convertNumbers2Arabic(tickValue)*/
      return progressType === ProgressType.FORMULA
        ? '٪' + tickValue
        : progressType === ProgressType.CALORIES
        ? Math.round(tickValue)
        : tickValue >= 1000
        ? Math.round(tickValue)
        : tickValue
    }
    return progressType === ProgressType.FORMULA
      ? tickValue + '%'
      : progressType === ProgressType.CALORIES
      ? Math.round(tickValue)
      : tickValue >= 1000
      ? Math.round(tickValue / 1000) + 'k'
      : tickValue
  }

  const xAxisTickFormat = (d: Date) => {
    if (progressPeriod === ProgressPeriod.WEEK) {
      return moment(d).format('ddd')
    }
    return moment(d).format('D')
  }

  const updateIntegration = () => {
    props.setOnboardingStatusToActivityData()
  }

  const normalTabStyle = { minWidth: '20%' }
  const tabStyle15 = { minWidth: '15%' }
  const tabStyle13 = { minWidth: '15%' }
  const tabStylePartial = { minWidth: '22%' }
  const disabledTabStyle = { opacity: 0.5, minWidth: '20%' }

  const barColor = (v: any) => {
    if (!moment(v.datum.date).isBefore(new Date())) return darkGray
    return v.datum.sum >= goalValue ? darkGreen : primaryBlue
  }

  const barStrokeWidth = (v: any) => {
    return moment(v.datum.date).isBefore(new Date()) ? 1 : 0
  }

  const width = 350
  const sectionWidth = width / data.length

  const tooltipLabel = ({ datum }: any) => {
    let number = datum[progressValueIndicator]
    let unit = ''
    if (progressType === ProgressType.FORMULA) {
      unit = isRtl ? '٪' : '%'
    } else if (progressType === ProgressType.STEPS) {
      unit = ' ' + t`steps`
    } else if (progressType === ProgressType.DISTANCE) {
      number = formatThousands(number, 2)
      unit = ' ' + (showMiles ? unitsCountLengthMileShort : unitsCountLengthKilometerShort)
    } else if (progressType === ProgressType.CALORIES) {
      unit = ' ' + unitsCountEnergyKilocalorieShort
    } else if (progressType === ProgressType.INTENSIVE_MINUTES) {
      unit = ' ' + unitsCountDurationMinuteShort
    }
    return isMobile && periodDays > 7
      ? `${number}${unit}\n${moment(datum.date).format('D. MMMM')}`
      : `${moment(datum.date).format('D. MMMM')} – ${number}${unit}`
  }

  const unitsLabel = getUnit(
    progressType,
    [ProgressType.DISTANCE, ProgressType.INTENSIVE_MINUTES].includes(progressType) ? customType : null,
    null,
  ) as string
  const isDistance = progressType === ProgressType.DISTANCE

  // @ts-ignore
  return (
    <Card>
      <NotificationsWrapper />
      <Grid
        container={true}
        justifyContent="space-between"
        alignContent="center"
        alignItems="center"
        style={{ margin: '0 0 1rem' }}
      >
        <Grid item>
          <h1 style={{ marginBottom: 3 }}>{t`Progress`}</h1>
        </Grid>

        <Grid item style={{ marginLeft: isRtl ? '0' : 'auto' }}>
          <Grid container alignItems="center">
            <Hidden smDown>
              <Grid item>
                {
                  <div style={{ fontWeight: 600 }}>
                    <svg
                      style={{ marginTop: -2, marginRight: isRtl ? 0 : 8, marginLeft: isRtl ? 8 : 0 }}
                      width="16"
                      height="16"
                      viewBox="0 0 12 12"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <rect y="1" width="12" height="11" rx="2" fill="#006FFF" />
                      <rect x="2" width="2" height="2" rx="1" fill="#006FFF" />
                      <rect x="2" y="4" width="2" height="2" rx="1" fill="white" />
                      <rect x="2" y="7" width="2" height="2" rx="1" fill="white" />
                      <rect x="8" width="2" height="2" rx="1" fill="#006FFF" />
                      <rect x="8" y="4" width="2" height="2" rx="1" fill="white" />
                      <rect x="5" y="4" width="2" height="2" rx="1" fill="white" />
                      <rect x="5" y="7" width="2" height="2" rx="1" fill="white" />
                    </svg>
                    {getFormattedDates(startDate, endDate)}
                  </div>
                }
              </Grid>
            </Hidden>
            <Grid item>
              <YumProgressPeriod progressPeriod={progressPeriod} setProgressPeriodDates={setProgressPeriodDates} />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      {
        <div className={'mobile-dates'} style={{ fontWeight: 600 }}>
          <svg
            style={{ marginTop: -4, marginRight: isRtl ? 0 : 8, marginLeft: isRtl ? 8 : 0 }}
            width="16"
            height="16"
            viewBox="0 0 12 12"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <rect y="1" width="12" height="11" rx="2" fill="#006FFF" />
            <rect x="2" width="2" height="2" rx="1" fill="#006FFF" />
            <rect x="2" y="4" width="2" height="2" rx="1" fill="white" />
            <rect x="2" y="7" width="2" height="2" rx="1" fill="white" />
            <rect x="8" width="2" height="2" rx="1" fill="#006FFF" />
            <rect x="8" y="4" width="2" height="2" rx="1" fill="white" />
            <rect x="5" y="4" width="2" height="2" rx="1" fill="white" />
            <rect x="5" y="7" width="2" height="2" rx="1" fill="white" />
          </svg>
          {getFormattedDates(startDate, endDate)}
        </div>
      }
      <YumTabs
        className={'progress__tabs user-select-none'}
        variant="scrollable"
        value={progressType}
        onChange={onProgressTypeChange}
        style={{
          marginBottom:
            ![ProgressType.DISTANCE, ProgressType.INTENSIVE_MINUTES].includes(progressType) && showActivities ? 48 : 0,
        }}
      >
        <YumTab style={normalTabStyle} label={t`Steps`} value={ProgressType.STEPS} />
        <YumTab style={normalTabStyle} label={t`Distance`} value={ProgressType.DISTANCE} />
        <YumTab style={normalTabStyle} label={unitsDisplayDurationCalorieLong} value={ProgressType.CALORIES} />
        <YumTab style={normalTabStyle} label={unitsDisplayDurationMinuteLong} value={ProgressType.INTENSIVE_MINUTES} />
        <YumTab
          label={t`Activity %`}
          value={ProgressType.FORMULA}
          disabled={!hasActivityIndex}
          style={!hasActivityIndex ? disabledTabStyle : normalTabStyle}
        />
      </YumTabs>

      {showActivities && (
        <>
          {progressType === ProgressType.INTENSIVE_MINUTES &&
            (!isTablet ? (
              <YumTabs
                className={'progress__tabs user-select-none full-width'}
                variant="fullWidth"
                value={customType}
                onChange={onCustomTypeChange}
                style={{
                  marginTop: 10,
                }}
                scrollButtons="desktop"
              >
                <YumTab style={tabStyle15} label={t`Exercise minutes`} value={CustomType.EXERCISE_MIN} />
                <YumTab style={tabStyle15} label={t`Intensive minutes`} value={CustomType.INTENSIVE_MINUTES} />
                <YumTab style={tabStyle13} label={t`Mindfulness`} value={CustomType.MINDFULNESS_MIN} />
                <YumTab label={t`Sleep`} value={CustomType.SLEEP_MIN} />
                <YumTab label={t`Walking`} value={CustomType.WALKING_MIN} />
                <YumTab label={t`Running`} value={CustomType.RUNNING_MIN} />
                <YumTab label={t`Cycling`} value={CustomType.CYCLING_MIN} />
                <YumTab label={t`Swimming`} value={CustomType.SWIMMING_MIN} />
                <YumTab label={t`Yoga`} value={CustomType.YOGA_MIN} />
              </YumTabs>
            ) : (
              <YumTabs
                className={'progress__tabs user-select-none full-width'}
                variant="scrollable"
                value={customType}
                onChange={onCustomTypeChange}
                style={{
                  marginTop: 10,
                }}
                selectionFollowsFocus={true}
              >
                <YumTab style={normalTabStyle} label={t`Exercise minutes`} value={CustomType.EXERCISE_MIN} />
                <YumTab style={normalTabStyle} label={t`Intensive minutes`} value={CustomType.INTENSIVE_MINUTES} />
                {/*<YumTab style={normalTabStyle} label={t`Mindfulness`} value={CustomType.MINDFULNESS_MIN} />
                <YumTab style={normalTabStyle} label={t`Sleep`} value={CustomType.SLEEP_MIN} />*/}
                <YumTab style={normalTabStyle} label={t`Walking`} value={CustomType.WALKING_MIN} />
                <YumTab style={normalTabStyle} label={t`Running`} value={CustomType.RUNNING_MIN} />
                <YumTab style={normalTabStyle} label={t`Cycling`} value={CustomType.CYCLING_MIN} />
                <YumTab style={normalTabStyle} label={t`Swimming`} value={CustomType.SWIMMING_MIN} />
                <YumTab style={normalTabStyle} label={t`Yoga`} value={CustomType.YOGA_MIN} />
              </YumTabs>
            ))}

          {progressType === ProgressType.DISTANCE && (
            <YumTabs
              className={'progress__tabs user-select-none'}
              variant="scrollable"
              value={customType}
              onChange={onCustomTypeChange}
              style={{ marginTop: 10 }}
            >
              <YumTab
                style={normalTabStyle}
                label={t`Total`}
                value={showMiles ? CustomType.DISTANCE_MI : CustomType.DISTANCE_KM}
              />
              <YumTab
                style={normalTabStyle}
                label={t`Walking`}
                value={showMiles ? CustomType.WALKING_MI : CustomType.WALKING_KM}
              />
              <YumTab
                style={normalTabStyle}
                label={t`Running`}
                value={showMiles ? CustomType.RUNNING_MI : CustomType.RUNNING_KM}
              />
              <YumTab
                style={normalTabStyle}
                label={t`Cycling`}
                value={showMiles ? CustomType.CYCLING_MI : CustomType.CYCLING_KM}
              />
              <YumTab style={normalTabStyle} label={t`Swimming`} value={CustomType.SWIMMING_M} />
            </YumTabs>
          )}
        </>
      )}

      {!hasActivityIndex && progressType !== ProgressType.FORMULA ? (
        <div className="activity-index-info">
          <span>
            {currentLang === Lang.EN || currentLang === Lang.ET
              ? t`If you want to know what your YuMuuv activity level is, you have to provide additional data.`
              : t`If you want to know what your YuMuuv activity level is, you have to provide additional data and connect a heart rate activity device.`}
          </span>
          {` `}
          <a href={undefined} onClick={updateIntegration}>{t`Start setup`}</a>
        </div>
      ) : (
        ''
      )}
      {progressType === ProgressType.DISTANCE && <YumInsufficientPermissionsWrapper special={true} />}
      {/*{distanceScopeMissing && progressType === ProgressType.DISTANCE && (
          <div className="activity-index-info">
            <div>{t`Distance`}</div>
            <span>{t`We seem to have insufficient permissions to sync with your ${serviceProvider} integration.`}</span>
            {` `}
            <a href={undefined} onClick={handleGoogleFitUnverified}>{t`Reauthorize`}</a>
          </div>
        )}*/}

      {!data.length ? (
        <Loader />
      ) : (
        <>
          <div className={'user-select-none'}>
            <svg style={{ height: 0 }}>
              <defs>
                <linearGradient id="myGradient">
                  <stop offset="0%" stopColor="red" />
                  <stop offset="25%" stopColor="orange" />
                  <stop offset="50%" stopColor="gold" />
                  <stop offset="75%" stopColor="yellow" />
                  <stop offset="100%" stopColor="green" />
                </linearGradient>
              </defs>
            </svg>
            <VictoryChart
              theme={VictoryTheme}
              height={isMobile ? 200 : 150}
              padding={{
                top: isMobile ? 20 : 10,
                bottom: 50,
                right: isRtl ? chartLeftPadding : chartRightPadding,
                left: isRtl ? chartRightPadding : chartLeftPadding,
              }}
              maxDomain={{ y: maxDomainValue }}
              containerComponent={<VictoryVoronoiContainer voronoiDimension="x" />}
              domainPadding={{ x: sectionWidth }}
            >
              <VictoryAxis
                dependentAxis
                offsetX={isRtl ? chartLeftPadding : chartRightPadding}
                orientation={isRtl ? 'left' : 'right'}
                tickFormat={yAxisTickFormat}
                style={{
                  tickLabels: {
                    width: 500,
                    fontSize: chartFontSize,
                    fill: yAxisTickLabelsFill,
                  },
                  axis: { stroke: 'none' },
                  ticks: { stroke: 'none' },
                  grid: {
                    stroke: yAxisGridStroke,
                    strokeWidth: yAxisGridStrokeWidth,
                    strokeDasharray: yAxisGridStrokeDasharray,
                  },
                }}
                tickValues={tickValues}
              />
              <VictoryAxis
                style={{
                  tickLabels: {
                    fontSize: chartFontSize,
                  },
                  grid: { stroke: 'none' },
                }}
                tickCount={isMobile && ProgressPeriod.MONTH === progressPeriod ? Math.ceil(periodDays / 4) : periodDays}
                tickFormat={xAxisTickFormat}
              />
              <VictoryBar
                dataComponent={
                  <Bar
                    style={{
                      stroke: barColor,
                      strokeWidth: barStrokeWidth,
                      fill: barColor,
                    }}
                  />
                }
                x="date"
                y={progressValueIndicator}
                data={data}
                cornerRadius={{ top: progressPeriod === ProgressPeriod.WEEK ? 2 : 1 }}
                labels={tooltipLabel}
                labelComponent={
                  <VictoryTooltip
                    cornerRadius={chartTooltipCornerRadius}
                    pointerWidth={chartTooltipPointerWidth}
                    pointerLength={chartTooltipPointerLength}
                    flyoutPadding={chartTooltipFlyoutPadding}
                    dy={chartTooltipDy}
                    flyoutStyle={{
                      stroke: barColor,
                      strokeWidth: chartTooltipStrokeWidth,
                      fill: barColor,
                    }}
                    style={{
                      fontSize: chartTooltipFontSize,
                      fill: 'white',
                      fontWeight: 400,
                      fontFamily: 'Helvetica Neue',
                    }}
                  />
                }
              />
            </VictoryChart>
          </div>

          <YumLogTable
            data={props.dailyDiary}
            valueGoal={goalValue}
            units={progressType === ProgressType.FORMULA ? t`Activity index` : unitsLabel}
            showDigits={isDistance}
            manualEntryEnabled={true}
            suffix={progressType === ProgressType.FORMULA ? '%' : ''}
            showTotalSum={true}
            showSeparateValues={true}
          />
        </>
      )}
    </Card>
  )
}
